import { useEffect, useRef, useState } from 'react';
import { useClerk, useAuth as useClerkAuth, useUser } from '@clerk/clerk-react';
import { Loader2 } from 'lucide-react';
import { Button } from '@artstorefronts/ui';

interface ClerkGuardProps {
  children: React.ReactNode;
}

interface ClerkUserWithReload {
  reload: () => Promise<void>;
}

const POLL_INTERVAL_MS = 500;
const MAX_POLL_ATTEMPTS = 10;

const PROVISION_QUERY = '{ __typename }';

async function provisionAsfUser(getToken: () => Promise<string | null>): Promise<void> {
  const apiUrl = import.meta.env.VITE_API_URL;
  if (!apiUrl) {
    console.warn('[ClerkGuard] VITE_API_URL is unset; cannot provision');
    return;
  }

  let token: string | null = null;
  try {
    token = await getToken();
  } catch (err) {
    console.error('[ClerkGuard] getToken failed', err);
    return;
  }
  if (!token) return;

  try {
    await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'x-clerk-token': 'true',
        'apollo-require-preflight': 'true',
      },
      body: JSON.stringify({ query: PROVISION_QUERY }),
    });
  } catch (err) {
    console.error('[ClerkGuard] provisioning request failed', err);
  }
}

export function ClerkGuard({ children }: ClerkGuardProps) {
  const { loaded } = useClerk();
  const { isSignedIn, getToken } = useClerkAuth();
  const { user: clerkUser, isLoaded: clerkUserLoaded } = useUser();
  const [pollState, setPollState] = useState<'idle' | 'polling' | 'timeout'>('idle');
  const attemptsRef = useRef(0);
  const clerkUserRef = useRef(clerkUser);
  const provisionedRef = useRef(false);

  clerkUserRef.current = clerkUser;

  useEffect(() => {
    if (!loaded || !clerkUserLoaded) return;

    const isUserReady = isSignedIn && clerkUser;
    const hasAsfId = clerkUser?.publicMetadata?.asfId;

    if (!isUserReady || hasAsfId) {
      if (pollState !== 'idle') {
        setPollState('idle');
        attemptsRef.current = 0;
        provisionedRef.current = false;
      }
      return;
    }

    if (pollState === 'idle') {
      attemptsRef.current = 0;
      setPollState('polling');
    }

    // Fire a one-shot authenticated request to trigger the API's inline create
    // (findOrCreateByClerkToken), which writes publicMetadata.asfId on Clerk.
    // Decoupled from the pollState gate so that re-mounts (StrictMode, HMR)
    // can still send the request when needed.
    if (!provisionedRef.current) {
      provisionedRef.current = true;
      provisionAsfUser(getToken).catch((err) => {
        console.warn('[ClerkGuard] Failed to trigger provisioning request:', err);
      });
    }
  }, [loaded, clerkUserLoaded, isSignedIn, clerkUser, pollState, getToken]);

  useEffect(() => {
    if (pollState !== 'polling') return;

    const pollInterval = setInterval(async () => {
      const user = clerkUserRef.current;
      if (!user) return;

      attemptsRef.current++;

      if ('reload' in user && typeof user.reload === 'function') {
        try {
          await (user as unknown as ClerkUserWithReload).reload();
        } catch (err) {
          console.warn('[ClerkGuard] Failed to reload user:', err);
        }
      }

      if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
        console.error(`[ClerkGuard] Timeout after ${MAX_POLL_ATTEMPTS} attempts waiting for asfId`);
        setPollState('timeout');
        clearInterval(pollInterval);
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(pollInterval);
  }, [pollState]);

  if (!loaded) return null;

  const isWaiting = pollState === 'polling' || pollState === 'timeout';

  if (isWaiting) {
    return (
      <div className="flex flex-col justify-center items-center h-screen gap-4 px-4">
        {pollState === 'timeout' ? (
          <div className="text-center max-w-md">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load Account</h2>
            <p className="text-sm text-gray-600 mb-4">
              We're having trouble loading your account information. This may be a temporary issue. Please try
              refreshing the page or contact support if the problem persists.
            </p>
            <div className="flex flex-col gap-2">
              <Button onClick={() => window.location.reload()}>Refresh Page</Button>
            </div>
          </div>
        ) : (
          <>
            <Loader2 className="w-8 h-8 animate-spin" />
            <p className="text-sm text-gray-600">Loading your account...</p>
          </>
        )}
      </div>
    );
  }

  return <>{children}</>;
}
